import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import { atom, useAtom } from 'jotai';
import { useDebounceFn } from 'ahooks';
import type { PageInfo } from 'generated/graphql';
import {
    CONTACT_SUGGESTIONS_API_ROUTE,
    type loader,
} from '~/routes/remix-api.chat.contact-suggestions';
import {
    CONVERSATIONS_API,
    type loader as conversationsLoader,
} from '~/routes/remix-api.chat.conversations';
import { useUserLoggedIn } from '~/modules/user';
import type { ContactSuggestion, Conversation } from '../types';

/**
 * The cached contact suggestions and pagination information for the current
 * user with no filters applied.
 */
const contactSuggestionsCacheAtom = atom<{
    suggestions: ContactSuggestion[];
    pageInfo?: PageInfo;
} | null>(null);

/** Maximum number of conversations to show. */
const CONVERSATIONS_LIMIT = 6;

/**
 * Hook to get contact suggestions for the current user. When `includeChats` is
 * `true`, also fetches conversations matching the search query.
 */
export function useContactSuggestions({
    limit = 50,
    initialExcludeMemberIDs = [],
    includeChats = false,
}: {
    /**
     * The maximum number of suggestions to return. Do not change this value.
     * Defaults to 50.
     */
    limit?: number;
    /**
     * The member IDs to exclude from the results. Can be updated with `setSearch`.
     */
    initialExcludeMemberIDs?: number[];
    /**
     * Also fetch conversations in addition to members. Defaults to `false`.
     */
    includeChats?: boolean;
} = {}) {
    const [cache, setCache] = useAtom(contactSuggestionsCacheAtom);
    const isFetched = useRef(false);
    const isConversationsFetched = useRef(false);
    const [search, setSearch] = useState('');
    const [excludeMemberIDs, setExcludeMemberIDs] = useState<number[]>(
        initialExcludeMemberIDs,
    );
    const [offset, setOffset] = useState(0);
    const [suggestions, setSuggestions] = useState<ContactSuggestion[]>(
        cache?.suggestions ?? [],
    );
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(
        cache?.pageInfo,
    );
    const fetcher = useFetcher<typeof loader>();
    const conversationsFetcher = useFetcher<typeof conversationsLoader>();
    const apiUrl = useMemo(() => {
        const url = new URL(
            CONTACT_SUGGESTIONS_API_ROUTE,
            window.location.origin,
        );
        url.searchParams.set('q', search);
        url.searchParams.set('limit', String(limit));
        url.searchParams.set('offset', String(offset));
        for (const excludeMemberID of excludeMemberIDs) {
            url.searchParams.append(
                'excludeMemberIDs',
                String(excludeMemberID),
            );
        }
        return `${url.pathname}${url.search}`;
    }, [search, excludeMemberIDs, limit, offset]);
    const conversationsApiUrl = useMemo(() => {
        if (!includeChats) return null;
        const url = new URL(CONVERSATIONS_API, window.location.origin);
        url.searchParams.set('q', search);
        url.searchParams.set('limit', String(CONVERSATIONS_LIMIT));
        return `${url.pathname}${url.search}`;
    }, [includeChats, search]);

    const { run: setSearchDebounced, flush } = useDebounceFn(
        (newSearch: string, offset: number, excludeMemberIDs?: number[]) => {
            setSearch(newSearch);
            setOffset(offset);
            if (excludeMemberIDs) {
                setExcludeMemberIDs(excludeMemberIDs);
            }
        },
        { wait: 300 },
    );

    const setSearchCallback = useCallback(
        (newSearch: string, excludeMemberIDs?: number[]) => {
            newSearch = newSearch.trim();
            setSearchDebounced(
                newSearch,
                // Reset offset when search changes.
                0,
                excludeMemberIDs,
            );
            if (!newSearch) {
                setSuggestions(cache?.suggestions ?? []);
                setPageInfo(cache?.pageInfo);
                flush();
            }
        },
        [cache, setSearchDebounced, flush],
    );

    const loadMore = useCallback(() => {
        if (fetcher.state !== 'idle' || !pageInfo?.hasNextPage) {
            return;
        }
        setOffset(suggestions.length);
    }, [pageInfo, fetcher.state, suggestions.length]);

    useEffect(() => {
        if (!fetcher.data && fetcher.state === 'idle' && !isFetched.current) {
            // Fetch initial suggestions on mount.
            fetcher.load(apiUrl);
            isFetched.current = true;
        } else if (isFetched.current) {
            // Refetch when the query params change.
            fetcher.load(apiUrl);
            // Reset fetcher.data to avoid caching the filtered suggestions.
            fetcher.data = undefined;
        }
        // Must not include `fetcher` to avoid infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl]);

    // Fetch conversations when includeChats is enabled.
    useEffect(() => {
        if (!includeChats || !conversationsApiUrl) return;
        if (
            !conversationsFetcher.data &&
            conversationsFetcher.state === 'idle' &&
            !isConversationsFetched.current
        ) {
            // Fetch initial conversations on mount.
            conversationsFetcher.load(conversationsApiUrl);
            isConversationsFetched.current = true;
        } else if (isConversationsFetched.current) {
            // Refetch when the query params change.
            conversationsFetcher.load(conversationsApiUrl);
            // Reset fetcher.data to avoid caching the filtered conversations.
            conversationsFetcher.data = undefined;
        }
        // Must not include `conversationsFetcher` to avoid infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includeChats, conversationsApiUrl]);

    // Set data from fetcher and cache results.
    useEffect(() => {
        if (!fetcher.data || fetcher.state !== 'idle') {
            return;
        }
        const newSuggestions = fetcher.data.suggestions.nodes;
        const newPageInfo = fetcher.data.suggestions.pageInfo;
        setSuggestions((prev) => {
            const next = newPageInfo.hasPreviousPage
                ? [...prev]
                : newSuggestions;
            if (newPageInfo.hasPreviousPage) {
                for (const suggestion of newSuggestions) {
                    if (!prev.some((s) => s.ID === suggestion.ID)) {
                        next.push(suggestion);
                    }
                }
            }
            if (!search.trim() && !excludeMemberIDs.length) {
                setCache({ suggestions: next, pageInfo: newPageInfo });
            }
            return next;
        });
        setPageInfo(newPageInfo);
    }, [search, excludeMemberIDs, fetcher, setCache]);

    // Set conversations data from fetcher.
    useEffect(() => {
        if (!includeChats) {
            setConversations([]);
            return;
        }
        if (
            !conversationsFetcher.data ||
            conversationsFetcher.state !== 'idle'
        ) {
            return;
        }
        setConversations(conversationsFetcher.data.conversations);
    }, [includeChats, conversationsFetcher]);

    return {
        /**
         * The list of contact suggestions (members).
         */
        suggestions,
        /**
         * The list of conversations. Only populated when `includeChats` is `true`.
         * Returns up to n conversations matching the search query, sorted by
         * latest message.
         */
        conversations: includeChats ? conversations : undefined,
        /**
         * There are more contact suggestions to load.
         */
        hasMore: pageInfo?.hasNextPage ?? false,
        /**
         * The suggestions are currently being loaded.
         */
        isLoading: fetcher.state !== 'idle',
        /**
         * The conversations are currently being loaded (only when `includeChats`
         * is true).
         */
        isLoadingConversations:
            includeChats && conversationsFetcher.state !== 'idle',
        /**
         * Set the search value to filter the suggestion results. Optionally
         * include a list of member IDs to exclude from the results. Changing
         * the search params will trigger a call to the API after a short delay (debounced).
         */
        setSearch: setSearchCallback,
        /**
         * Load more suggestions by fetching the next page. This increments the
         * offset and appends the new results to the existing suggestions.
         */
        loadMore,
    };
}

/**
 * Hook to fetch and cache the initial contact suggestions for the current user.
 * This is used to prefetch the results to avoid loading states. This hook should
 * only be called once in the component tree.
 */
export function useContactSuggestionsCache() {
    const { isLoggedIn } = useUserLoggedIn();
    const [cache, setCache] = useAtom(contactSuggestionsCacheAtom);
    const isFetched = useRef(false);
    const fetcher = useFetcher<typeof loader>({
        key: 'contact-suggestions-cache',
    });

    useEffect(() => {
        if (cache || !isLoggedIn) return;
        if (!fetcher.data && fetcher.state === 'idle' && !isFetched.current) {
            // Fetch initial suggestions on mount.
            fetcher.load(CONTACT_SUGGESTIONS_API_ROUTE);
            isFetched.current = true;
        } else if (fetcher.data) {
            setCache({
                suggestions: fetcher.data.suggestions.nodes,
                pageInfo: fetcher.data.suggestions.pageInfo,
            });
        }
    }, [cache, isLoggedIn, fetcher, setCache]);

    // Clear the contact suggestions cache when the user logs out.
    useEffect(() => {
        if (!isLoggedIn) {
            setCache({ suggestions: [] });
            isFetched.current = false;
        }
    }, [isLoggedIn, setCache]);
}
